import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Snackbar,
  createMuiTheme,
  ThemeProvider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import { Link, withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { red } from "@material-ui/core/colors";
import {
  MobileButton,
  MobileOutlinedButton,
  MobileRegularPage,
  MobileTextField,
  MobileAlert,
  repliValidator,
} from "../../../components";
import MobileLoading from "../../../components/MobileLoading";
import { localStorageCleanup } from "../../../services/util.service";
import styles from "./MyAccount.style";
import Cookies from "universal-cookie";

const cookies = new Cookies();

const deleteButtonTheme = createMuiTheme({
  palette: {
    primary: red,
  },
});

const MyAccountTextField = ({ ...rest }) => {
  return <MobileTextField fullWidth {...rest} />;
};

const AccountRemoveDialog = (props) => {
  return (
    <Dialog
      open={props.open}
      onClose={props.onDialogClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">Account Removal</DialogTitle>
      <DialogContent>
        <DialogContentText>
          To confirm the removal of your account, please type <i>delete</i>{" "}
          below
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          type="text"
          placeholder="delete"
          fullWidth
          value={props.text}
          onChange={props.onTextChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onDialogCancel} color="primary">
          Cancel
        </Button>
        <Button
          onClick={props.onDialogOK}
          color="primary"
          disabled={props.text !== "delete"}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MyAccount = (props) => {
  const classes = styles();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionResult, setActionResult] = useState({});
  const [openRemoveAccountDialog, setOpenRemoveAccountDialog] = useState(false);
  const [accountRemoveConfirmationText, setAccountRemoveConfirmationText] =
    useState("");
  const [deleting, setDeleting] = useState(false);
  const [id, setId] = useState();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [organizations, setOrganizations] = useState("");
  const [department, setDepartment] = useState("");
  const [availableEmail, setAvailableEmail] = useState(true);
  const [emailUnavailbilityMessage, setEmailUnavailbilityMessage] =
    useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const validator = useRef(
    repliValidator({
      validators: {
        password_confirmation: {
          message: t("common.message.validation.passwordConfirmation"),
          rule: (val, params, validator) => {
            return val === params[0];
          },
          required: true,
        },
      },
    })
  );



  useEffect(() => {
    const loadAccountInfo = async () => {
      try {
        let response = await fetch(
          `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_AUTH}/app/me`,
          {
            headers: {
              "X-ACCESS-TOKEN": cookies.get("token"),
            },
          }
        );
        if (!response) return;
        let user = await response.json();
        setId(user._id);
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setEmail(user.email);
        setOrganizations(user.organizations);
        setDepartment(user.department)
        validator.current.showMessages();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    loadAccountInfo();
  }, []);

  useEffect(() => {
    if (actionResult.severity === "success") {
      setTimeout(() => props.history.push("/app/dashboard"), 2000);
    }
  }, [actionResult, props.history]);

  useEffect(() => {
    setEmailUnavailbilityMessage(
      availableEmail
        ? ""
        : t("module.provider.addPatient.message.duplicatedEmail")
    );
  }, [availableEmail, t]);

  const checkDuplicatedEmail = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_PATIENT_GETBYEMAIL
        }?${new URLSearchParams({
          email: email,
          id: id,
        })}`,
        {
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );
      if (!response.ok) {
        setAvailableEmail(false);
        props.validator.showMessageFor("email");
      } else {
        setAvailableEmail(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getPostParameters = () => {
    let params = new URLSearchParams({
      id,
      email,
      firstName,
      lastName,
    });
    if (password) {
      params.append("password", password);
      params.append("passwordConfirmation", passwordConfirmation);
    }

    return params;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const isValid = validator.current.allValid();
    if (!isValid) return;

    setSaving(true);
    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_USER}/app/myaccount`,
        {
          method: "post",
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
          body: getPostParameters(),
        }
      );

      if (!response.ok) {
        let json = await response.json();
        setActionResult({ severity: "error", message: json.message });
        return;
      }
      setActionResult({
        severity: "success",
        message: t("module.provider.myAccount.message.editAccountSuccess"),
      });
    } catch (error) {
      console.log(error);
      setActionResult({
        severity: "error",
        message: `${t(
          "module.provider.myAccount.message.editAccountFailure"
        )}. ${t("common.message.error.seeLog")}.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMessageClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setActionResult({});
  };

  const handleAccountRemoveDialogOK = async () => {
    if (accountRemoveConfirmationText !== "delete") return;
    setDeleting(true);
    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_USER}/app/myaccount`,
        {
          method: "delete",
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );

      if (!response.ok) {
        let json = await response.json();
        setActionResult({ severity: "error", message: json.message });
        return;
      }
      await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_AUTH_SIGNOUT}`
      );
      localStorageCleanup();
      cookies.remove("token", { path: "/" });
      props.history.push("/app");
    } catch (error) {
      console.log(error);
      setActionResult({
        severity: "error",
        message: `${t(
          "module.provider.myAccount.message.editAccountFailure"
        )}. ${t("common.message.error.seeLog")}.`,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <MobileLoading />;

  return (
    <MobileRegularPage>
      <Box className={classes.title}>
        <Box>
          <AccountCircleIcon style={{ fontSize: "90" }} />
        </Box>
        <Typography variant="h3">
          {t("module.provider.myAccount.title")}
        </Typography>
      </Box>
      <form className={classes.form}>
        <MyAccountTextField
          label={`${t("signUp.inputPlaceholders.firstName")}*`}
          value={firstName}
          error={!validator.current.fieldValid("firstName")}
          onChange={(e) => setFirstName(e.target.value.trimStart())}
          helperText={validator.current.message(
            "firstName",
            firstName,
            "required|alpha_space"
          )}
          autoFocus
        />
        <MyAccountTextField
          label={`${t("signUp.inputPlaceholders.lastName")}*`}
          value={lastName}
          error={!validator.current.fieldValid("lastName")}
          onChange={(e) => setLastName(e.target.value.trimStart())}
          helperText={validator.current.message(
            "lastName",
            lastName,
            "required|alpha"
          )}
        />
        <MyAccountTextField
          label={`${t("common.form.label.email")}*`}
          autoComplete="email"
          // InputProps={{ readOnly: true }}
          error={!validator.current.fieldValid("email") || !availableEmail}
          helperText={
            validator.current.message("email", email, "required|email") ||
            emailUnavailbilityMessage
          }
          onChange={(e) => setEmail(e.target.value)}
          onBlur={checkDuplicatedEmail}
          value={email}
        />

        {
          organizations && organizations.length > 0 && <MyAccountTextField
            label={`${t("common.form.label.organization")}`}
            InputProps={{ readOnly: true }}
            value={organizations}
            disabled={true}
          />
        }
        {
          department && department !== '' && <MyAccountTextField
            label={`${t("common.form.label.department")}`}
            InputProps={{ readOnly: true }}
            value={department}
            disabled={true}
          />
        }
        
        <MyAccountTextField
          label={t("signUp.inputPlaceholders.password")}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value.trim())}
          error={!validator.current.fieldValid("password")}
          helperText={
            validator.current.message("password", password, "min:8") ||
            t("module.provider.editUser.message.passwordHelpText")
          }
        />

        <MyAccountTextField
          label={t("signUp.inputPlaceholders.passwordConfirmation")}
          type="password"
          value={passwordConfirmation}
          onChange={(e) => setPasswordConfirmation(e.target.value.trim())}
          error={!validator.current.fieldValid("passwordConfirmation")}
          helperText={validator.current.message(
            "passwordConfirmation",
            passwordConfirmation,
            `password_confirmation:${password}`
          )}
        />
      </form>
      <Box className={classes.buttonContainer}>
        <Box>
          <MobileButton
            label={t("common.form.button.save")}
            className={classes.button}
            onClick={handleSave}
            loading={saving}
          />
        </Box>
        <Box>
          <MobileOutlinedButton
            className={classes.button}
            component={Link}
            to="/app/dashboard"
            label={t("signUp.buttonLabels.cancel")}
            onClick={props.scrollToTop}
            disabled={saving}
          />
        </Box>
        <ThemeProvider theme={deleteButtonTheme}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            className={classes.button}
            disabled={deleting}
            onClick={() => setOpenRemoveAccountDialog(true)}
          >
            {t("common.form.button.delete")}
            {deleting && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </Button>
        </ThemeProvider>
      </Box>
      <Snackbar
        open={actionResult.message}
        autoHideDuration={6000}
        onClose={handleMessageClose}
      >
        <MobileAlert
          onClose={handleMessageClose}
          severity={actionResult.severity}
        >
          {actionResult.message}
        </MobileAlert>
      </Snackbar>
      <AccountRemoveDialog
        open={openRemoveAccountDialog}
        text={accountRemoveConfirmationText}
        onTextChange={(e) => setAccountRemoveConfirmationText(e.target.value)}
        onDialogCancel={() => setOpenRemoveAccountDialog(false)}
        onDialogOK={handleAccountRemoveDialogOK}
      />
    </MobileRegularPage>
  );
};

export default withRouter(MyAccount);